import type { Problem } from "@malfak/contracts";

/** Client-side problem: an org-scoped call with no current organization (a login with no membership). */
export const NO_ORGANIZATION = "no_organization";
export const NO_ORGANIZATION_TYPE = "https://malfak.app/errors/no_organization";

/** An API failure: RFC 9457 problem details when the server sent them. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;
  readonly requestId: string | null;
  readonly route: string;

  constructor(opts: { status: number; problem?: Problem | null; requestId?: string | null; route: string; message?: string }) {
    super(opts.message ?? opts.problem?.detail ?? opts.problem?.title ?? `Request failed (${opts.status})`);
    this.name = "ApiError";
    this.status = opts.status;
    this.problem = opts.problem ?? null;
    this.requestId = opts.requestId ?? null;
    this.route = opts.route;
  }

  get isNetwork() {
    return this.status === 0;
  }
  get isUnauthorized() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isConflict() {
    return this.status === 409;
  }
  get isRateLimited() {
    return this.status === 429;
  }
  /** No current organization: the UI sends the user to "Create your organization". */
  get isNoOrganization() {
    return this.problem?.title === NO_ORGANIZATION;
  }
  get isValidation() {
    return this.status === 400 || this.status === 422;
  }
  /** Field errors from `details.fields` (if the API sent them). */
  get fieldErrors(): Record<string, string> {
    const f = this.problem?.details?.["fields"];
    return f && typeof f === "object" ? (f as Record<string, string>) : {};
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
